class ProductVariants {
  constructor(section) {
    this.section = section;
    this.form = section.querySelector('[data-product-form]');
    this.productJson = section.querySelector('[data-product-json]');
    this.variantMediaJson = section.querySelector('[data-variant-media-json]');
    this.variantInput = section.querySelector('[data-variant-id]');
    this.stickyVariantInput = section.querySelector('[data-sticky-variant-id]');
    this.price = section.querySelector('[data-price]');
    this.stickyPrice = section.querySelector('[data-sticky-price]');
    this.addButton = section.querySelector('[data-add-button]');
    this.addButtonText = section.querySelector('[data-add-button-text]');
    this.stickyBuyButton = section.querySelector('[data-sticky-buy-button]');
    this.stickyBuyButtonText = section.querySelector('[data-sticky-buy-button-text]');
    this.mainImage = section.querySelector('[data-main-product-image]');
    this.thumbnails = section.querySelectorAll('[data-media-thumbnail]');
    this.optionLabels = section.querySelectorAll('[data-selected-option]');

    if (!this.form || !this.productJson) return;

    this.product = JSON.parse(this.productJson.textContent);
    this.variantMedia = this.variantMediaJson ? JSON.parse(this.variantMediaJson.textContent) : {};
    // Listen on the whole section, not just the form: the variant picker
    // (color swatches / option pills) renders outside the <form> tag so
    // app blocks can be placed between it and the buy buttons.
    this.section.addEventListener('change', this.onChange.bind(this));
    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => this.selectThumbnail(thumbnail));
    });
  }

  getSelectedOptions() {
    return Array.from(this.section.querySelectorAll('[data-option-input]:checked'))
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((input) => input.value);
  }

  getSelectedVariant() {
    const selectedOptions = this.getSelectedOptions();
    return this.product.variants.find((variant) => {
      const variantOptions = this.getVariantOptions(variant);
      return selectedOptions.every((option, index) => variantOptions[index] === option);
    });
  }

  getVariantOptions(variant) {
    if (Array.isArray(variant.options)) return variant.options;

    return [variant.option1, variant.option2, variant.option3].filter(Boolean);
  }

  onChange() {
    const selectedOptions = this.getSelectedOptions();
    const selectedVariant = this.getSelectedVariant();

    this.optionLabels.forEach((label) => {
      const index = Number(label.dataset.selectedOption) - 1;
      label.textContent = selectedOptions[index] || '';
    });

    if (!selectedVariant) {
      this.setUnavailable();
      return;
    }

    this.variantInput.value = selectedVariant.id;
    if (this.stickyVariantInput) this.stickyVariantInput.value = selectedVariant.id;
    this.price.textContent = this.formatMoney(selectedVariant.price);
    if (this.stickyPrice) this.stickyPrice.textContent = this.formatMoney(selectedVariant.price);

    if (selectedVariant.available) {
      this.addButton.disabled = false;
      this.addButtonText.textContent = this.addButton.dataset.addLabel || 'Add to cart';
      if (this.stickyBuyButton) this.stickyBuyButton.disabled = false;
      if (this.stickyBuyButtonText) this.stickyBuyButtonText.textContent = this.stickyBuyButton.dataset.buyLabel || 'Buy Now';
    } else {
      this.setUnavailable(this.addButton.dataset.soldOutLabel || 'Sold out');
    }

    this.selectVariantMedia(selectedVariant);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', selectedVariant.id);
    window.history.replaceState({}, '', url.toString());
  }

  selectThumbnail(thumbnail) {
    if (!this.mainImage || !thumbnail.dataset.mediaSrc) return;

    this.mainImage.src = thumbnail.dataset.mediaSrc;
    this.mainImage.removeAttribute('srcset');
    this.mainImage.alt = thumbnail.dataset.mediaAlt || this.mainImage.alt;
    this.mainImage.dataset.mediaId = thumbnail.dataset.mediaId;

    this.thumbnails.forEach((item) => {
      const isActive = item === thumbnail;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    this.scrollThumbnailIntoView(thumbnail);
  }

  selectMedia(mediaId) {
    const thumbnail = Array.from(this.thumbnails).find((item) => item.dataset.mediaId === mediaId);
    if (thumbnail) this.selectThumbnail(thumbnail);
  }

  scrollThumbnailIntoView(thumbnail) {
    const gallery = thumbnail.closest('.product-thumbnails');
    if (!gallery) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const galleryRect = gallery.getBoundingClientRect();
    const thumbnailRect = thumbnail.getBoundingClientRect();
    const offset =
      thumbnailRect.left -
      galleryRect.left -
      gallery.clientWidth / 2 +
      thumbnail.clientWidth / 2;

    gallery.scrollBy({
      left: offset,
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });
  }

  selectVariantMedia(variant) {
    const mappedMediaId = this.variantMedia[String(variant.id)];
    const featuredMediaId =
      (variant.featured_media && variant.featured_media.id) ||
      variant.featured_media_id ||
      (variant.featured_image && variant.featured_image.id);
    const mediaId = mappedMediaId || featuredMediaId;

    if (mediaId) this.selectMedia(String(mediaId));
  }

  setUnavailable(text) {
    this.addButton.disabled = true;
    this.addButtonText.textContent = text || this.addButton.dataset.unavailableLabel || 'Unavailable';
    if (this.stickyBuyButton) this.stickyBuyButton.disabled = true;
    if (this.stickyBuyButtonText) {
      this.stickyBuyButtonText.textContent = text || this.stickyBuyButton.dataset.unavailableLabel || 'Unavailable';
    }
  }

  formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }

    const currency = window.Shopify?.currency?.active || 'USD';

    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency
    }).format(cents / 100);
  }
}

class HeroSlider {
  constructor(hero) {
    this.hero = hero;
    this.slides = Array.from(hero.querySelectorAll('[data-hero-slide]'));
    this.index = 0;

    if (this.slides.length < 2) return;

    window.setInterval(() => this.showNext(), 5200);
  }

  showNext() {
    this.slides[this.index].classList.remove('is-active');
    this.index = (this.index + 1) % this.slides.length;
    this.slides[this.index].classList.add('is-active');
  }
}

class MobileMenu {
  constructor(header) {
    this.header = header;
    this.toggle = header.querySelector('[data-mobile-menu-toggle]');
    this.menu = header.querySelector('[data-mobile-menu]');

    if (!this.toggle || !this.menu) return;

    this.toggle.addEventListener('click', () => this.toggleMenu());
    this.menu.addEventListener('click', (event) => {
      if (event.target.matches('a')) this.close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
  }

  toggleMenu() {
    const isOpen = this.toggle.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.menu.hidden = false;
    this.header.classList.add('is-menu-open');
    this.toggle.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.header.classList.remove('is-menu-open');
    this.toggle.setAttribute('aria-expanded', 'false');
    window.setTimeout(() => {
      if (!this.header.classList.contains('is-menu-open')) {
        this.menu.hidden = true;
      }
    }, 180);
  }
}

class SmartHeader {
  constructor(header) {
    this.header = header;
    this.lastY = window.scrollY;
    this.ticking = false;
    this.threshold = 12;
    this.hideAfter = 140;

    this.setHeaderHeight();
    window.addEventListener('resize', () => this.setHeaderHeight());
    window.addEventListener('scroll', () => this.requestUpdate(), { passive: true });
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty('--site-header-height', `${this.header.offsetHeight}px`);
  }

  requestUpdate() {
    if (this.ticking) return;

    window.requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
    this.ticking = true;
  }

  update() {
    const currentY = window.scrollY;
    const delta = currentY - this.lastY;

    if (Math.abs(delta) < this.threshold) {
      this.lastY = currentY;
      return;
    }

    if (this.header.classList.contains('is-menu-open') || currentY <= this.hideAfter || delta < 0) {
      this.header.classList.remove('is-scroll-hidden');
    } else {
      this.header.classList.add('is-scroll-hidden');
    }

    this.lastY = currentY;
  }
}

class RevealEffects {
  constructor() {
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.items = Array.from(document.querySelectorAll([
      '.section-heading',
      '.product-card',
      '.feature-card',
      '.product__media',
      '.product__info',
      '.product-trust__panel',
      '.step-card',
      '.comparison__inner',
      '.testimonial-card',
      '.content-card',
      '.content-faq',
      '.b2b-card',
      '.b2b-split',
      '.b2b-contact',
      '.contact-page__hero',
      '.contact-page__quick article',
      '.contact-card'
    ].join(',')));

    if (!this.items.length) return;

    this.items.forEach((item, index) => {
      item.dataset.reveal = '';
      item.dataset.revealDelay = String(index % 4);
    });

    if (this.prefersReducedMotion || !('IntersectionObserver' in window)) {
      this.items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => this.onIntersect(entries),
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    this.items.forEach((item) => this.observer.observe(item));
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      entry.target.classList.add('is-visible');
      this.observer.unobserve(entry.target);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-site-header]').forEach((header) => {
    new MobileMenu(header);
    new SmartHeader(header);
  });

  document.querySelectorAll('[data-product-section]').forEach((section) => {
    new ProductVariants(section);
  });

  document.querySelectorAll('[data-hero-slider]').forEach((hero) => {
    new HeroSlider(hero);
  });

  new RevealEffects();
});
